import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { compareListLabels } from '../CompareList/consts';
import { tourType } from '../../../util/sharedPropTypes';
import getComparisonRows from './getComparisonRows';
import Icon from '../Atoms/Icon';
import useCurrency from '../../../hooks/useCurrency';
import Picture from '../Picture';
import formatPrice from '../../../util/formatPrice';
import getMaximumLengths from './getMaximumLengths';
import { below } from '../../../util/mediaQuery';
import useCustomHorizontalScrollbar from '../../../hooks/useCustomHorizontalScrollbar';
import { adjustAccordionHeights, calculateMaxTourTitleHeight, TOUR_TITLE_CLASS } from './helpers';
import Loading from '../Core/Loading';

const propTypes = {
  comparePackageList: PropTypes.arrayOf(tourType).isRequired,
  maximumCompareAmount: PropTypes.number.isRequired,
  labels: compareListLabels.isRequired,
  onRemovePackageItem: PropTypes.func.isRequired,
  onRemoveAllPackageItems: PropTypes.func.isRequired,
  isCompareKeyListLoading: PropTypes.bool.isRequired,
  packageItemRefs: PropTypes.shape({
    current: PropTypes.arrayOf(PropTypes.instanceOf(Element)),
  }),
  contentRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
}.isRequired;

function PackageComparisonModalContent({
  comparePackageList,
  maximumCompareAmount,
  labels,
  onRemovePackageItem,
  onRemoveAllPackageItems,
  isCompareKeyListLoading,
  packageItemRefs,
  contentRef,
}) {
  const [gridTemplateColumns, setGridTemplateColumns] = useState('');
  const [nestedGridTemplateColumns, setNestedGridTemplateColumns] = useState('');
  const [nestedGridStartColumn, setNestedGridStartColumn] = useState(0);
  const [comparisonContentIsLoading, setComparisonContentIsLoading] = useState(true);

  const [currentCurrencyCodeAndSymbol] = useCurrency(comparePackageList[0].displayPricePerCurrency);

  const { customScrollbarRef, thumbRef, thumbStyle, isScrollable } = useCustomHorizontalScrollbar(
    comparisonContentIsLoading,
    contentRef
  );

  const comparisonGridRef = useRef(null);
  const accordionRefs = useRef({});

  const handleAccordionToggle = (label, isNowOpen) => {
    setTimeout(() => {
      adjustAccordionHeights(label, isNowOpen, accordionRefs);
    }, 0);
  };

  const rows = getComparisonRows(labels);

  const numberOfEmptyPlaceholderColumnsUpToMax = maximumCompareAmount - comparePackageList.length;
  const emptyPlaceholderColumnsUpToMaxArray = [
    ...Array(numberOfEmptyPlaceholderColumnsUpToMax),
  ].map((item, index) => ({ id: index }));

  const rowsWithMultipleItems = [
    labels.hotelLineItemText,
    labels.motorhomeLineItemText,
    labels.campingLineItemText,
    labels.extraLineItemText,
    labels.excursionLineItemText,
  ];

  useEffect(() => {
    const handleWindowResize = () => {
      let columnWidth = 372;
      let labelColumnWidth = 346; // Use numbers, not strings
      let nestedGridStartColumnNumber = 2;

      if (below('desktopLarge')) {
        columnWidth = 290;
        labelColumnWidth = 190;
      }

      if (below('mobileLarge')) {
        columnWidth = 316;
        nestedGridStartColumnNumber = 1; // as the label column is removed on mobile
        labelColumnWidth = 0; // No sticky column on mobile
      }

      // Update the CSS variable with units
      document.documentElement.style.setProperty('--sticky-column-width', `${labelColumnWidth}px`);

      const gridTemplateColumnsCss = below('mobileLarge')
        ? `repeat(${maximumCompareAmount}, ${columnWidth}px)`
        : `${labelColumnWidth}px repeat(${maximumCompareAmount}, ${columnWidth}px)`;

      // Explicitly set width on the comparison grid based on gridTemplateColumns calculation to ensure
      // sticky behaves correctly.
      const totalWidth = below('mobileLarge')
        ? maximumCompareAmount * columnWidth
        : labelColumnWidth + maximumCompareAmount * columnWidth;

      if (comparisonGridRef.current) {
        comparisonGridRef.current.style.width = `${totalWidth}px`;
      }

      const nestedGridTemplateColumnsCss = `repeat(${comparePackageList.length}, ${columnWidth}px)`;

      setGridTemplateColumns(gridTemplateColumnsCss);
      setNestedGridTemplateColumns(nestedGridTemplateColumnsCss);
      setNestedGridStartColumn(nestedGridStartColumnNumber);
      if (comparisonContentIsLoading) {
        setComparisonContentIsLoading(false);
      }
    };

    const debouncedHandleWindowResize = debounce(handleWindowResize, 200);

    debouncedHandleWindowResize();
    window.addEventListener('resize', debouncedHandleWindowResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleWindowResize);
    };
  }, [comparePackageList, maximumCompareAmount]);

  useLayoutEffect(() => {
    calculateMaxTourTitleHeight();
    window.addEventListener('resize', calculateMaxTourTitleHeight);
    return () => {
      window.removeEventListener('resize', calculateMaxTourTitleHeight);
    };
  }, [comparisonContentIsLoading]);

  const renderCustomHorizontalScrollBar = () => (
    <div
      className="PackageComparisonModalContent__customScrollbar"
      ref={customScrollbarRef}
      style={{ display: isScrollable ? 'block' : 'none' }}
    >
      <div
        className="PackageComparisonModalContent__customScrollbarThumb"
        ref={thumbRef}
        style={thumbStyle}
      />
    </div>
  );

  if (comparisonContentIsLoading)
    return (
      <div className="PackageComparisonModalContent__loading">
        <Loading />
      </div>
    );

  const setPackageItemRef = (el, index) => {
    // eslint-disable-next-line no-param-reassign
    packageItemRefs.current[index] = el;
  };

  return (
    <div className="PackageComparisonModalContent" ref={contentRef}>
      <div className="PackageComparisonModalContent__header">
        <h2 className="PackageComparisonModalContent__heading">{labels.modalTitleText}</h2>
        {renderCustomHorizontalScrollBar()}
      </div>
      <div
        className="ComparisonGrid"
        role="table"
        aria-label="Package Comparison Table"
        style={{ gridTemplateColumns }}
        ref={comparisonGridRef}
      >
        {/* Table Header */}
        <div className="ComparisonGrid__header" role="rowgroup">
          {/* Nested Row 1: Tour Images */}
          <div className="ComparisonGrid__row" role="row">
            {/*  Nested Row 1, Column 1: Package Number Container (spans 3 rows) */}
            <div
              className="ComparisonGrid__cell ComparisonGrid__packageNumberContainer"
              role="cell"
            >
              <div className="ComparisonGrid__packageNumberCircle">
                <span className="ComparisonGrid__packageNumberText">
                  {comparePackageList.length}
                </span>
                <span className="ComparisonGrid__packageNumberText ComparisonGrid__packageNumberText-mobile">
                  {labels.modalCompactComparingText
                    ?.replace('{Count}', comparePackageList.length)
                    ?.replace('{MaxCompare}', maximumCompareAmount)}
                </span>
              </div>
              <div className="ComparisonGrid__comparingText">
                {labels.modalComparingText
                  ?.replace('{Count}', comparePackageList.length)
                  ?.replace('{MaxCompare}', maximumCompareAmount)}
              </div>
              <button
                type="button"
                className="Button ComparisonGrid__clearAllButton"
                onClick={onRemoveAllPackageItems}
              >
                <div className="ComparisonGrid__clearIconContainer">
                  <Icon id="cross" blockName="ComparisonGrid__clearIcon" />
                </div>
                <div>{labels.modalRemoveAllCtaText}</div>
              </button>
            </div>

            {/*  Nested Row 1, Columns 2+: Tour Images */}
            {comparePackageList.map((tour, index) => (
              <div
                key={`image-${tour.key}`}
                className="ComparisonGrid__cell ComparisonGrid__imageCell"
                role="cell"
                ref={(el) => setPackageItemRef(el, index)}
              >
                <button
                  type="button"
                  className="Button ComparisonGrid__removeButton"
                  onClick={() => onRemovePackageItem(tour.key)}
                  disabled={isCompareKeyListLoading}
                >
                  <Icon id="cross" blockName="ComparisonGrid__removeButtonIcon" />
                </button>
                {tour?.image ? (
                  <Picture
                    {...tour.image}
                    className="ComparisonGrid__image"
                    sizes={[
                      [100, 1.77], // from 0
                      [100, 1.77], // from 480
                      [50, 1.77], // from 768
                      ['500', 1.77], // from 1024
                      ['500', 1.77], // from 1440
                    ]}
                  />
                ) : (
                  <div className="ComparisonGrid__imagePlaceholder">No Image</div>
                )}
              </div>
            ))}
            {emptyPlaceholderColumnsUpToMaxArray.map((column) => (
              <div
                className="ComparisonGrid__emptyCell ComparisonGrid__emptyImageCell"
                key={`emptyColumn-${column.id}-image`}
                role="presentation"
              />
            ))}
            {/* Nested Row 2: Tour Titles */}
            {/* Nested Row 2, Column 1: spanned by packageNumberContainer */}

            <div className="ComparisonGrid__row" role="row">
              {/* Nested Row 2, Columns 2+: Tour Images */}
              {comparePackageList.map((tour) => (
                <div
                  key={`title-${tour.key}`}
                  className={`ComparisonGrid__cell ComparisonGrid__headerCell ${TOUR_TITLE_CLASS}`}
                  role="columnheader"
                >
                  {tour.name || 'N/A'}
                </div>
              ))}
              {emptyPlaceholderColumnsUpToMaxArray.map((column) => (
                <div
                  className="ComparisonGrid__emptyCell"
                  key={`emptyColumn-${column.id}-title`}
                  role="presentation"
                />
              ))}
            </div>

            {/* Nested Row 3: Prices and View Button */}
            <div className="ComparisonGrid__row" role="row">
              {/* Nested Row 3, Column 1: spanned by packageNumberContainer */}
              {/* Nested Row 3, Columns 2+: Price and View Button  */}
              {comparePackageList.map((tour) => {
                const totalPrice =
                  tour.displayPricePerCurrency.find(
                    (priceByCurrency) =>
                      priceByCurrency.currency === currentCurrencyCodeAndSymbol.code
                  ).price || tour.displayPricePerCurrency[0].price; // Fallback to first currency if not found

                return (
                  <div
                    key={`price-${tour.key}`}
                    className="ComparisonGrid__cell ComparisonGrid__priceCell"
                    role="cell"
                  >
                    {!tour.isSoldOut && !tour.waitingListLink && (
                      <div className="ComparisonGrid__priceContainer">
                        <span className="ComparisonGrid__price">
                          {/* // include current currency */}
                          {labels.fromPriceText} {currentCurrencyCodeAndSymbol.symbol}
                          {formatPrice(totalPrice)}
                          {labels.perPersonText}
                        </span>
                        <a href={tour.url} className="ComparisonGrid__link">
                          <span className="ComparisonGrid__cta">
                            <span className="ComparisonGrid__ctaText">
                              {labels.viewTourCtaText}
                            </span>
                          </span>
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
              {emptyPlaceholderColumnsUpToMaxArray.map((column) => (
                <div
                  className="ComparisonGrid__emptyCell"
                  key={`emptyColumn-${column.id}-price`}
                  role="presentation"
                />
              ))}
            </div>

            {/* Nested Row 4: Icons (Inclusions) */}
            <div className="ComparisonGrid__row" role="row">
              {/* Nested Row 4: Column 1: Empty Cell */}
              <div
                className="ComparisonGrid__cell ComparisonGrid__labelCell ComparisonGrid__labelCell--empty"
                role="presentation"
              />
              {/* Nested Row 4: Column 2+ Inclusions Icons */}
              {comparePackageList.map((tour) => (
                <div
                  key={`icons-${tour.key}`}
                  className="ComparisonGrid__cell ComparisonGrid__iconsCell"
                  role="cell"
                >
                  {tour.icons && tour.icons.length > 0 ? (
                    <ul className="ComparisonGrid__iconsList">
                      {tour.icons.map((inclusion) => (
                        <li key={inclusion} className="ComparisonGrid__iconsItem">
                          <Icon id={inclusion} blockName="ComparisonGrid__icon" />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="ComparisonGrid__inclusionsPlaceholderText">No Inclusions</div>
                  )}
                </div>
              ))}
              {emptyPlaceholderColumnsUpToMaxArray.map((column) => (
                <div
                  className="ComparisonGrid__emptyCell"
                  key={`emptyColumn-${column.id}-inclusions`}
                  role="presentation"
                />
              ))}
            </div>
          </div>
        </div>
        {/* Table Body */}
        <div className="ComparisonGrid__body" role="rowgroup">
          {rows
            // Remove any unnecessary rows which all have negative values (maxLength of 0) as these should not appear in the modal
            .filter((row) => getMaximumLengths(row.label, labels, comparePackageList).maxLength > 0)
            // Map through those with results.
            .map((row, index) => {
              const maxLengths = getMaximumLengths(row.label, labels, comparePackageList);
              return (
                <div key={row.label} className="ComparisonGrid__row" role="row">
                  {/* Column 1: Label Cell */}
                  <div className="ComparisonGrid__cell ComparisonGrid__labelCell" role="rowheader">
                    <div className="ComparisonGrid__labelText">{row.label}</div>
                  </div>
                  {/* Columns 2+: Data Cells */}
                  {rowsWithMultipleItems.includes(row.label) ? (
                    <div
                      className="ComparisonGrid__nestedGrid"
                      style={{
                        gridTemplateColumns: nestedGridTemplateColumns,
                        gridTemplateRows: `repeat(${
                          maxLengths.maxUpgradesLength > 0
                            ? maxLengths.maxLength + 1
                            : maxLengths.maxLength
                        }, auto)`,
                        gridColumn: `${nestedGridStartColumn} / ${
                          comparePackageList.length + nestedGridStartColumn
                        }`,
                      }}
                    >
                      {comparePackageList.map((tour, columnIndex) =>
                        row.getValue(
                          tour,
                          maxLengths,
                          accordionRefs,
                          columnIndex,
                          handleAccordionToggle
                        )
                      )}
                    </div>
                  ) : (
                    comparePackageList.map((tour, columnIndex) => (
                      <div
                        key={`${row.label}-${tour.key}`}
                        className="ComparisonGrid__cell ComparisonGrid__dataCell"
                        role="cell"
                      >
                        {row.getValue(
                          tour,
                          maxLengths,
                          accordionRefs,
                          columnIndex,
                          handleAccordionToggle
                        )}
                      </div>
                    ))
                  )}
                  {/* Empty placeholder columns */}
                  {emptyPlaceholderColumnsUpToMaxArray.map((column) => (
                    <div
                      className={`ComparisonGrid__emptyCell ${
                        rows.length - 1 === index ? 'ComparisonGrid__emptyCell--lastRow' : ''
                      }`}
                      key={`emptyColumn-${column.id}-tourDetails`}
                      role="presentation"
                    />
                  ))}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

PackageComparisonModalContent.propTypes = propTypes;
export default PackageComparisonModalContent;
